import * as React from 'react';
import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { State } from 'reducers';
import { push as navigateTo, goBack } from 'connected-react-router';
import { getBuildingIdByUrlSlug, HasUrlSlug } from 'selectors/buildingsSelectors';
import { editTheme } from 'actions/editorActions';
import { clearUploadTheme } from 'actions/themeActions';
import { ThemePropertiesDialog } from '../themePropertiesDialog/ThemePropertiesDialog';
import { makeNewTheme } from '../makeNewTheme';
import { useParams } from 'util/reactRouterHooks';

export type Props = {
    visible: boolean;
    isNorthAmerica: boolean;
};

export const NewThemeDialog = ({ visible, isNorthAmerica }: Props) => {
    const { t } = useTranslation();

    const params = useParams<HasUrlSlug>();
    const { urlSlug } = params;
    const buildingId = useSelector((state: State) => getBuildingIdByUrlSlug(state, params));
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    const dispatch = useDispatch();

    const handleAcceptNewTheme = useCallback(({ displayName, deviceType, width, height, uploaded, requirements }: any) => {
        setIsSubmitting(true);
        const theme = makeNewTheme(displayName, deviceType, width, height, uploaded, requirements, isNorthAmerica, t);
        if (uploaded) {
            dispatch(navigateTo(`/buildings/${urlSlug}/themes`));
            dispatch(clearUploadTheme());
        }
        else if (buildingId) {
            // Show the editor with a new theme
            dispatch(editTheme(theme, { buildingId }));
            dispatch(navigateTo(`/buildings/${urlSlug}/themes/${theme.id}/edit`));
        }
        setIsSubmitting(false);
    }, [ dispatch, urlSlug, buildingId, t, setIsSubmitting, isNorthAmerica ]);

    const handleCancelNewTheme = useCallback(() => {
        dispatch(goBack());
        dispatch(clearUploadTheme());
    }, [ dispatch ]);

    if (!urlSlug || !buildingId) {
        return null;
    }

    return (
        <ThemePropertiesDialog
            visible={ visible }
            buildingId={ buildingId }
            onAccept={ handleAcceptNewTheme }
            onCancel={ handleCancelNewTheme }
            isNew={ true }
            isSubmitting={ isSubmitting }
        />
    );
};
